import { objectHandler } from '@frontend/jetlend-core/src/ducks/object';
import { simpleFormHandler } from '@frontend/jetlend-core/src/ducks/form';
import {
    isEmptyValue,
    required,
    requireEmail,
    requirePhone,
} from '@frontend/jetlend-core/src/validators';
import {
    call,
    delay,
    put,
    select,
} from 'redux-saga/effects';
import { addToastError } from '@frontend/jetlend-core/src/ducks/toasts';
import { storeHandler } from '@frontend/jetlend-core/src/ducks/store';
import { simpleSagaHandlerV2 } from '@frontend/jetlend-core/src/ducks/saga_v2';
import { ApiDataResponse } from '@frontend/jetlend-core/src/models/api';
import { modalHandler } from '@frontend/jetlend-core/src/ducks/modal';
import {
    ICalculatorState,
    ICreditCalculatorResult,
} from '@app/models/features/borrowers';
import {
    ClientType,
    IPhoneFormModel,
} from '@app/models/common/common';
import { sendEvent } from '@app/ducks/common/analytics';
import { startRegistration } from '@app/ducks/common/registration';
import {
    BorrowerLimitPrescoringContacts,
    BorrowerLimitPrescoringInitialData,
    BorrowerLimitPrescoringModalStage,
    BorrowerLimitPrescoringRequest,
    BorrowerLimitPrescoringStatus,
    BorrowerLimitPrescoringStatusType,
    BorrowerLimitPrescoringStoreData,
} from '@app/models/borrowers/limitPrescoring';
import {
    apiGetLimitPrescoringStatus,
    apiPostLimitPrescoring,
    apiPostLimitPrescoringRegister,
} from '@app/services/client/borrowers/limitPrescoringService';
import { signDataWithSmsSaga } from '../common/sms';

export const VERSION = 2;
export const PREFIX = 'borrowers';
/**
 * Интервал опроса статуса прескоринга в миллисекундах
 */
export const LIMIT_PRESCORING_POOLING_INTERVAL = 30000;
/**
 * Регулярное выражение для проверки ИНН физического лица
 */
export const INDIVIDUAL_INN_REGEXP = /^\d{12}$/;
/**
 * Регулярное выражение для проверки ИНН юридического лица
 */
export const COMPANY_INN_REGEXP = /^\d{10}$/;


export const calculatorHandler = objectHandler<ICalculatorState>(PREFIX, 'calculator', () => ({
    loanAmount: 10000000,
    loanDuration: 36,
    revenueAmount: 5000000,
    companyAge: true,
    creditHistory: true,
    stableRevenue: true,
    employeeAmount: true,
}));

export const summaryHandler = objectHandler<ICreditCalculatorResult>(PREFIX, 'summary', () => ({
    probability_of_approval: 0,
    min_interest_rate_amount: 0,
    max_interest_rate_amount: 0,
    monthly_payment: 0,
}));

export const borrowersPhoneFormHandler = simpleFormHandler<IPhoneFormModel>(
    PREFIX, 'borrower-phone', {
        v2: {
            phone: [ required(), requirePhone() ],
        },
    }, {
        *onBeforeSubmit (values) {
            yield put(sendEvent('borrower-button'));

            return values;
        },
        apiMethod: () => ({ status: 'OK' }),
        *onSuccess(_, values) {
            yield put(startRegistration(ClientType.BORROWER, values.phone));
        },
    }, {}
);

/**
 * Стор для хранения данных связанных с прескорингом заёмщика.
 */
export const limitPrescoringStoreHandler = storeHandler<BorrowerLimitPrescoringStoreData>(
    PREFIX,
    'limit-prescoring-status',
    {
        getInitialState: () => ({ scoring_status: BorrowerLimitPrescoringStatusType.NONE }),
    }
);

/**
 * Хендлер модального окна прескоринга лимита для заемщика.
 */
export const limitPrescoringModalHandler = modalHandler(PREFIX, 'limit_prescoring_modal', 'limit_prescoring_modal');

/**
 * Хендлер попапа с предложением ознакомиться с результатом прескоринга лимита для заемщика.
 */
export const limitPrescoringResultsPopupHandler = modalHandler(PREFIX, 'limit_prescoring_results_popup', 'limit_prescoring_results_popup');

export const startPrescoringSagaHandler = simpleSagaHandlerV2(PREFIX, 'start_prescoring', function* () {
    const isOpen = yield select(limitPrescoringModalHandler.isOpen);

    if (!isOpen) {
        // Если модалка не открыта, то открываем её
        yield put(limitPrescoringModalHandler.open());
    }
});

/**
 * Открыть модальное окно прескоринга.
 */
export const openPrescoringModal = () => startPrescoringSagaHandler.action();

/**
 * Обработчик изменения стадии модального окна прескоринга в зависимости от статуса
 */
function* updatePrescoringModalStage(newStatus?: BorrowerLimitPrescoringStatusType) {
    const prescoringStoreData: BorrowerLimitPrescoringStoreData = yield select(limitPrescoringStoreHandler.selector);

    const status = newStatus ?? prescoringStoreData.scoring_status;

    let stage;

    // Меняем модальное окно в зависимости от статуса прескоринга
    switch (status) {
    case BorrowerLimitPrescoringStatusType.APPROVE:
        stage = BorrowerLimitPrescoringModalStage.APPROVE;
        break;
    case BorrowerLimitPrescoringStatusType.REJECT:
        stage = BorrowerLimitPrescoringModalStage.REJECT;
        break;
    default:
        // Если нет решения о одобрении / неодобрении - оставляем стадию без изменений
        stage = prescoringStoreData.stage;
        break;
    }

    // Обновляем статус прескоринга в хранилище
    yield put(limitPrescoringStoreHandler.update({
        stage,
    }));
}

/**
 * Хендлер опроса статуса прескоринга.
 */
export const limitPrescoringStatusPoolingHandler = simpleFormHandler(
    PREFIX, 'limit-prescoring-pooling', {}, {
        apiMethod: apiGetLimitPrescoringStatus,
        *onSuccess(response, values) {
            const prescoringStoreData: BorrowerLimitPrescoringStoreData = yield select(limitPrescoringStoreHandler.selector);

            const data: BorrowerLimitPrescoringStatus = response.data;

            const inn = values.inn;

            if (!data || !inn) {
                return;
            }

            // Обновляем результат прескоринга
            yield put(limitPrescoringStoreHandler.update({
                inn,
                ...data,
            }));

            // Проверяем ввёл ли пользователь контакты
            const isContactsReceived = prescoringStoreData.email && prescoringStoreData.phone;

            if (isContactsReceived) {
                // Меняем модальное окно в зависимости от статуса прескоринга
                yield call(updatePrescoringModalStage, data.scoring_status);
            }

            // Если прескоринг в процессе - повторяем запрос через интервал
            if (data.scoring_status === BorrowerLimitPrescoringStatusType.UPDATING) {
                yield delay(LIMIT_PRESCORING_POOLING_INTERVAL);
                return yield put(limitPrescoringStatusPoolingHandler.submit({ inn }));
            }

            // В случае ошибки - отображаем тост с ошибкой
            if (data.scoring_status === BorrowerLimitPrescoringStatusType.ERROR) {
                return yield put(addToastError('Ошибка при получении лимита для заемщика'));
            }
        },
        *onFormError(message) {
            yield put(addToastError(message || 'Ошибка при получении лимита для заемщика'));
        },
    }, {}
);

/**
 * Хендлер запроса прескоринга лимита для заемщика по ИНН.
 */
export const limitPrescoringRequestFormHandler = simpleFormHandler<BorrowerLimitPrescoringInitialData, ApiDataResponse<BorrowerLimitPrescoringRequest>>(
    PREFIX, 'limit-prescoring-request', {
        v2: {
            inn: [],
            extraValidate: (values, errors) => {
                if (!isEmptyValue(values.inn)) {
                    if (!values.inn.match(COMPANY_INN_REGEXP) && !values.inn.match(INDIVIDUAL_INN_REGEXP)) {
                        errors.inn = 'Введите корректный ИНН';
                    }
                }
            },
        },
    }, {
        *onBeforeSubmit(values) {
            yield put(sendEvent('borrower-button'));

            return values;
        },
        apiMethod: apiPostLimitPrescoring,
        *onSuccess(response, values) {
            // Начинаем опрашивать статус прескоринга
            if (!response?.data?.has_account && values.inn) {
                yield put(limitPrescoringStatusPoolingHandler.submit({ inn: values.inn }));
            }

            // Если заемщик уже есть на платформе - предлагаем войти в личный кабинет,
            // иначе - предлагаем подтвердить контакты
            yield put(limitPrescoringStoreHandler.update({
                stage: response?.data?.has_account
                    ? BorrowerLimitPrescoringModalStage.HAS_ACCOUNT
                    : BorrowerLimitPrescoringModalStage.EMAIL_CONFIRM,
            }));
            yield put(openPrescoringModal());
        },
        *onFormError(message) {
            yield put(addToastError(message || 'Ошибка при запросе лимита'));
        },
    }, {}
);

/**
 * Хендлер формы отправки контактов для прескоринга лимита
 */
export const limitPrescoringRegisterFormHandler = simpleFormHandler<BorrowerLimitPrescoringContacts>(
    PREFIX, 'limit-prescoring-register', {
        v2: {
            phone: [required(), requirePhone()],
            email: [required(), requireEmail()],
        },
    }, {
        apiMethod: apiPostLimitPrescoringRegister,
        *onBeforeSubmit(values: BorrowerLimitPrescoringContacts) {

            // Переводим модальное окно в стадию ввода кода подтверждения
            yield put(limitPrescoringStoreHandler.update({
                stage: BorrowerLimitPrescoringModalStage.CODE_CONFIRM,
            }));

            // Отправляем SMS с кодом подтверждения
            const smsId = yield signDataWithSmsSaga(ClientType.BORROWER, values.phone, values, 'limit-prescoring-register');

            const prescoringStoreData = yield select(limitPrescoringStoreHandler.selector);
            const { inn } = prescoringStoreData;

            if (!smsId) {
                return undefined;
            }

            // Переводим модальное окно в стадию "в процессе"
            yield put(limitPrescoringStoreHandler.update({
                stage: BorrowerLimitPrescoringModalStage.PROCESS,
            }));

            return {
                ...values,
                sms_id: smsId,
                inn,
            };
        },
        *onSuccess(_, values) {
            // Добавляем данные контактов в хранилище
            yield put(limitPrescoringStoreHandler.update({
                email: values.email,
                phone: values.phone,
            }));

            // Обновляем стадию модального окна при необходимости
            yield call(updatePrescoringModalStage);

            // Отправляем событие о регистрации заемщика
            yield put(sendEvent('borrower--guest-registration'));
        },
        *onFormError(message) {
            yield put(limitPrescoringStoreHandler.update({
                error: message || 'Ошибка при отправке данных',
            }));

            yield put(addToastError(message || 'Ошибка при отправке данных'));
        },
    }, {}
);

/**
 * Функция обновления данных формы при выборе элемента из выпадающего списка dadata
 */
function* selectInnForPrescoringSaga(suggestion) {
    const data = suggestion.data;

    const inn = data?.inn;
    const companyData = JSON.stringify(suggestion);

    yield put(limitPrescoringRequestFormHandler.formChange({
        inn,
        company_data: companyData,
    }));

    yield put(limitPrescoringStoreHandler.update({
        inn,
        company_data: companyData,
    }));
}

/**
 * Обработчик выбора компании из списка подсказок при вводе ИНН
 */
export const selectAccountInnHandler = simpleSagaHandlerV2(
    PREFIX, 'SELECT_INN', selectInnForPrescoringSaga
);